import type { BoardState, HarmonyReport, PathwayStatus } from '@/types';
import { HARMONIC_INTERVALS } from '@/types';

/**
 * Analyzes the current board state and returns a harmony report.
 * Checks each pathway to see if the harmonic interval between its
 * source and target nodes matches the required interval.
 *
 * Intervals are symmetric: a P5 up (7 semitones) is the same as a P4 down,
 * so we check both directions when determining satisfaction.
 */
export function analyze(board: BoardState): HarmonyReport {
  const pathwayStatuses: PathwayStatus[] = board.pathways.map((pathway) => {
    const sourceNode = board.nodes.find((n) => n.id === pathway.source);
    const targetNode = board.nodes.find((n) => n.id === pathway.target);

    if (!sourceNode || !targetNode) {
      return {
        pathwayId: pathway.id,
        satisfied: false,
        currentDifference: -1,
        requiredDifference: -1,
        nearConsonant: false,
      };
    }

    const requiredDifference = HARMONIC_INTERVALS[pathway.harmonic] ?? 0;

    // Compute the circular difference (mod 12) — one direction
    const diff = ((sourceNode.frequency - targetNode.frequency) % 12 + 12) % 12;
    const inverseDiff = diff === 0 ? 0 : 12 - diff;

    // Satisfied if diff matches required in either direction
    const satisfied =
      diff === requiredDifference || inverseDiff === requiredDifference;

    // Near-consonant: within 1 semitone of target interval, but not satisfied
    const nearConsonant =
      !satisfied &&
      (Math.abs(diff - requiredDifference) <= 1 ||
        Math.abs(inverseDiff - requiredDifference) <= 1);

    // Report the smaller of the two diffs as the currentDifference
    const currentDifference = Math.min(diff, inverseDiff);

    return {
      pathwayId: pathway.id,
      satisfied,
      currentDifference,
      requiredDifference,
      nearConsonant,
    };
  });

  const satisfiedCount = pathwayStatuses.filter((p) => p.satisfied).length;
  const totalPathways = pathwayStatuses.length;
  const resonancePercentage =
    totalPathways === 0 ? 100 : Math.round((satisfiedCount / totalPathways) * 100);

  return {
    pathways: pathwayStatuses,
    resonancePercentage,
    totalPathways,
    satisfiedCount,
  };
}
